import type { RouteLocationRaw } from 'vue-router';
import type { OnboardingPopupTask } from '#layers/access/public';
import { settingsOverlayPath } from '#layers/global-settings/public';

export interface OnboardingPopupTaskConfig {
	labelKey: string;
	descriptionKey: string;
	actionKey: string;
	requiresCreatedWorkspace: boolean;
	to: (slug: string | null) => RouteLocationRaw;
}

/** Display-model row rendered by the popup checklist. */
export interface OnboardingPopupTaskRow {
	id: OnboardingPopupTask;
	label: string;
	description: string;
	actionLabel: string;
	done: boolean;
	to: RouteLocationRaw;
	disabled: boolean;
}

/**
 * Navigation contract for the onboarding popup checklist. Each task maps to the
 * route it takes the user to, its i18n label key, and whether it depends on the
 * created-workspace step (dependent rows are disabled until that step is done).
 */
export const onboardingPopupTaskConfig: Record<OnboardingPopupTask, OnboardingPopupTaskConfig> = {
	createdWorkspace: {
		labelKey: 'onboarding_popup.tasks.created_workspace',
		descriptionKey: 'onboarding_popup.task_details.created_workspace.description',
		actionKey: 'onboarding_popup.task_details.created_workspace.action',
		requiresCreatedWorkspace: false,
		to: () => ({ name: 'workspaces.create' }),
	},
	connectedDataSource: {
		labelKey: 'onboarding_popup.tasks.connected_data_source',
		descriptionKey: 'onboarding_popup.task_details.connected_data_source.description',
		actionKey: 'onboarding_popup.task_details.connected_data_source.action',
		requiresCreatedWorkspace: true,
		to: (slug) => ({ name: 'data-model.source.create', params: { workspace: slug ?? '' } }),
	},
	managedSchema: {
		labelKey: 'onboarding_popup.tasks.managed_schema',
		descriptionKey: 'onboarding_popup.task_details.managed_schema.description',
		actionKey: 'onboarding_popup.task_details.managed_schema.action',
		requiresCreatedWorkspace: true,
		to: (slug) => ({ name: 'data-model.sources', params: { workspace: slug ?? '' } }),
	},
	configuredPermissions: {
		labelKey: 'onboarding_popup.tasks.configured_permissions',
		descriptionKey: 'onboarding_popup.task_details.configured_permissions.description',
		actionKey: 'onboarding_popup.task_details.configured_permissions.action',
		requiresCreatedWorkspace: true,
		to: (slug) => ({ name: 'workspace.settings.access.policies.listing', params: { workspace: slug ?? '' } }),
	},
	connectedToStudio: {
		labelKey: 'onboarding_popup.tasks.connected_to_studio',
		descriptionKey: 'onboarding_popup.task_details.connected_to_studio.description',
		actionKey: 'onboarding_popup.task_details.connected_to_studio.action',
		requiresCreatedWorkspace: true,
		to: (slug) => ({ name: 'connect', params: { workspace: slug ?? '' } }),
	},
	connectedAiAssistant: {
		labelKey: 'onboarding_popup.tasks.connected_ai_assistant',
		descriptionKey: 'onboarding_popup.task_details.connected_ai_assistant.description',
		actionKey: 'onboarding_popup.task_details.connected_ai_assistant.action',
		requiresCreatedWorkspace: true,
		to: (slug) => ({ name: 'settings.ai', params: { workspace: slug ?? '' } }),
	},
	invitedTeam: {
		labelKey: 'onboarding_popup.tasks.invited_team',
		descriptionKey: 'onboarding_popup.task_details.invited_team.description',
		actionKey: 'onboarding_popup.task_details.invited_team.action',
		requiresCreatedWorkspace: false,
		to: () => settingsOverlayPath('organization/members'),
	},
};
